import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tabs, Modal, Form, Input, Button, Popconfirm, message } from "antd";
import { EditOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import isEqual from "lodash.isequal";
import CloseButton from "../../../components/Common/CloseButton/CloseButton";
import restaurantSchemaActions from "../../../redux/restaurantSchema/actions";
import "./index.css";

const {
  createNewRegion,
  getRegions,
  getReservationTable,
  editReservationRegion,
  deleteReservationRegion,
  reorderRegions,
} = restaurantSchemaActions;

const RestaurantSchema = () => {
  const dispatch = useDispatch();
  const [activeKey, setActiveKey] = useState();
  const [items, setItems] = useState();
  const [openAddZoneModal, setOpenAddZoneModal] = useState(false);
  const [openEditZoneModal, setOpenEditZoneModal] = useState(false);
  const newTabIndex = useRef(0);
  const [form] = Form.useForm();

  const { regions } = useSelector((state) => state.restaurantSchemaReducer);

  useEffect(() => {
    dispatch(getRegions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEqual(items, regions)) {
      setItems(regions);
      // Only set activeKey and fetch tables on initial load (no activeKey yet)
      if (!activeKey && regions.length > 0) {
        setActiveKey(regions[0]?.key);
        dispatch(getReservationTable(regions[0]?.key));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions]);

  const onChange = (newActiveKey) => {
    setActiveKey(newActiveKey);
    dispatch(getReservationTable(newActiveKey));
  };

  const addNewZone = () => {
    setOpenAddZoneModal(true);
  };

  const onEdit = (targetKey, action) => {
    if (action === "add") {
      addNewZone();
    }
  };

  const handleOkAddZone = () => {
    form.validateFields().then((values) => {
      const newActiveKey = `newTab${newTabIndex.current++}`;
      const newPanes = [...items];

      dispatch(
        createNewRegion({
          name: values.name,
          organizationId: parseInt(localStorage.getItem("organizationId")),
        })
      );

      setItems(newPanes);
      setActiveKey(newActiveKey);
      setOpenAddZoneModal(false);
      form.resetFields();
    });
  };

  const handleEditZone = () => {
    const values = form.getFieldsValue();
    if (values.name && values.name.trim() !== "") {
      dispatch(
        editReservationRegion({
          name: values.name,
          regionId: activeKey,
          organizationId: parseInt(localStorage.getItem("organizationId")),
        })
      );
    }
  };

  const handleDeleteRegion = (e) => {
    dispatch(
      deleteReservationRegion({
        regionId: activeKey,
        organizationId: parseInt(localStorage.getItem("organizationId")),
      })
    );
    setOpenEditZoneModal(false);
    message.success("Успешно изтриване на регион");
    form.resetFields();
  };

  const [editRegionsList, setEditRegionsList] = useState([]);

  const openEditModal = () => {
    if (items && items.length > 0) {
      setEditRegionsList(items.map((item) => ({ key: item.key, label: item.label })));
    }
    setOpenEditZoneModal(true);
  };

  const moveRegionInList = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= editRegionsList.length) return;
    const reordered = [...editRegionsList];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setEditRegionsList(reordered);
  };

  const saveRegionOrder = () => {
    const regionOrders = editRegionsList.map((item, i) => ({
      regionId: item.key,
      sortOrder: i,
    }));

    dispatch(
      reorderRegions({
        regionOrders,
        organizationId: parseInt(localStorage.getItem("organizationId")),
      })
    );
  };

  return (
    <>
      <Modal
        closeIcon={<CloseButton />}
        title="Създай Регион"
        open={openAddZoneModal}
        onOk={handleOkAddZone}
        onCancel={() => setOpenAddZoneModal(false)}
        className="reservation-create-zone-modal"
        footer={
          <Button type="primary" onClick={handleOkAddZone}>
            Добави
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Име на региона"
            rules={[
              { required: true, message: "Моля въведете име на региона" },
            ]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        closeIcon={<CloseButton />}
        title="Управление на региони"
        open={openEditZoneModal}
        onCancel={() => setOpenEditZoneModal(false)}
        className="reservation-edit-zone-modal"
        footer={
          <>
            <Popconfirm
              title="Изтриване на регион"
              description="Сигурни, че искате да изтриете този регион?"
              onConfirm={handleDeleteRegion}
              placement="bottom"
              okText="Изтрий"
              cancelText="Назад"
            >
              <Button danger>Изтрий текущия регион</Button>
            </Popconfirm>
            <Button type="primary" onClick={() => {
              handleEditZone();
              saveRegionOrder();
              setOpenEditZoneModal(false);
              form.resetFields();
            }}>
              Запази
            </Button>
          </>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Преименувай текущия регион"
          >
            <Input
              placeholder={
                items?.find((item) => item.key === activeKey)?.label ||
                "Име на региона"
              }
            />
          </Form.Item>
        </Form>

        {editRegionsList.length > 1 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Подредба на региони:</div>
            {editRegionsList.map((region, idx) => (
              <div
                key={region.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 8px",
                  marginBottom: 4,
                  background: region.key === activeKey ? "#e6f4ff" : "#fafafa",
                  borderRadius: 6,
                  border: region.key === activeKey ? "1px solid #91caff" : "1px solid #f0f0f0",
                }}
              >
                <span style={{ flex: 1 }}>{idx + 1}. {region.label}</span>
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={idx === 0}
                  onClick={() => moveRegionInList(idx, -1)}
                />
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={idx === editRegionsList.length - 1}
                  onClick={() => moveRegionInList(idx, 1)}
                />
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Tabs
        type="editable-card"
        onChange={onChange}
        activeKey={activeKey}
        onEdit={onEdit}
        items={items}
        tabBarExtraContent={
          <Button
            id="reservations-edit-region-button"
            onClick={openEditModal}
          >
            <EditOutlined />
          </Button>
        }
      />
    </>
  );
};
export default RestaurantSchema;
